import React from "react";
import { useLocation } from "react-router-dom";
import { Coffee, Globe, LogOut, Moon, Sun, SunMoon } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCamp } from "@/context/SettingsContext";
import { useStats, occupancyPct, occupancyColor } from "@/context/StatsContext";
import { useTheme } from "@/lib/theme";
import GlobalSearch from "@/components/layout/GlobalSearch";
import StockBell from "@/components/layout/StockBell";
import MailBell from "@/components/layout/MailBell";
import InspectionBell from "@/components/layout/InspectionBell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const LANGS = ["nl", "es", "en"];

const titleKey = {
  "/": "nav_dashboard",
  "/aankomend": "nav_zoekers",
  "/aankomsten": "nav_aankomend",
  "/reservaties": "nav_reservaties",
  "/verblijven": "nav_verblijven",
  "/vertrekkend": "nav_vertrekkend",
  "/afrekening": "nav_afrekening",
  "/verhuur": "nav_verhuur",
  "/verhuizen": "nav_verhuizen",
  "/verbeteringen": "nav_verbeteringen",
  "/gasten": "nav_gasten",
  "/verkopen": "nav_verkopen",
  "/facturen": "nav_facturen",
  "/interfaces": "nav_interfaces",
  "/admin": "nav_admin",
  "/admin/camping": "admin_camping",
  "/admin/gebruikers": "users",
  "/admin/plattegrond": "admin_map",
  "/admin/voorraad": "admin_stock",
  "/admin/inkomende-facturen": "admin_purchases",
  "/admin/uitgaande-facturen": "admin_sales_inv",
  "/admin/dagafsluiting": "admin_cashclose",
  "/admin/gastenboek": "admin_guestbook",
  "/admin/mailserver": "admin_email",
  "/admin/uren": "admin_hours",
  "/admin/omzet-medewerker": "admin_seller_revenue",
  "/admin/jaaroverzicht": "admin_season",
  "/admin/afstortingen": "admin_deposits",
  "/uren": "my_hours",
  "/mailbox": "nav_mailbox",
  "/admin/campings": "tenants",
  "/admin/kaart": "map_update",
  "/wachtwoord": "nav_wachtwoord",
};

export default function Topbar() {
  const { t, lang, changeLang } = useLang();
  const { user, logout } = useAuth();
  const { stats } = useStats();
  const { camp } = useCamp();
  const { mode, theme, toggle } = useTheme(camp.gps);
  const location = useLocation();
  const [leave, setLeave] = React.useState(null); // "logout" | "break"
  const [note, setNote] = React.useState("");
  const key = titleKey[location.pathname] || "nav_dashboard";
  const pct = occupancyPct(stats?.kpis);
  const color = occupancyColor(pct);

  const initials = (user?.name || user?.email || "CB").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header className="h-16 bg-card border-b border-border sticky top-0 z-30 grid grid-cols-[1fr_auto] lg:grid-cols-[minmax(0,1fr)_minmax(240px,30rem)_auto] items-center gap-2 sm:gap-4 px-3 sm:px-8" data-testid="topbar">
      <div className="min-w-0">
        <h1 className="font-display font-bold text-lg sm:text-xl tracking-tight text-foreground truncate" data-testid="page-title">
          {t(key)}
        </h1>
        {user?.tenant_name && (
          <p className="text-[11px] font-semibold text-primary/80 truncate" data-testid="active-tenant">{user.tenant_name}</p>
        )}
      </div>

      <div className="hidden lg:block"><GlobalSearch /></div>

      <div className="flex items-center justify-end gap-1.5 sm:gap-2.5">
        <div className="flex items-center gap-2 rounded-full bg-muted/70 px-2 py-1 sm:px-3 sm:py-1.5 border border-border" title={t("total_occupancy")} data-testid="occupancy-badge">
          <span className="hidden xl:inline text-xs font-medium text-muted-foreground">{t("total_occupancy")}</span>
          <span className="font-display font-extrabold text-base sm:text-xl leading-none tabular-nums" style={{ color }} data-testid="occupancy-pct">{pct}%</span>
        </div>

        <MailBell />
        <StockBell />
        <InspectionBell />

        <button onClick={toggle} data-testid="theme-toggle" data-theme-mode={mode}
          title={t(mode === "auto" ? "theme_auto_hint" : mode === "dark" ? "theme_light" : "theme_dark")}
          className={`relative grid h-8 w-8 sm:h-9 sm:w-9 place-items-center rounded-full border bg-muted/50 transition-colors active:scale-95 ${
            mode === "auto" ? "border-primary/50 text-primary" : "border-border text-muted-foreground hover:text-primary hover:border-primary/40"
          }`}>
          {mode === "auto" ? <SunMoon className="h-4 w-4" /> : theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {mode === "auto" && <span className="absolute -bottom-1 text-[8px] font-bold uppercase tracking-wide text-primary">auto</span>}
        </button>

        <button onClick={() => changeLang(LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length])} data-testid="lang-cycle"
          title={t("language")} className="sm:hidden grid h-8 w-8 place-items-center rounded-full bg-muted/70 border border-border text-[11px] font-bold text-primary">
          {lang.toUpperCase()}
        </button>

        <div className="hidden sm:flex items-center gap-1 bg-muted/70 border border-border rounded-full p-1">
          <Globe className="h-4 w-4 text-muted-foreground ml-1.5" />
          {LANGS.map((l) => (
            <button key={l} onClick={() => changeLang(l)} data-testid={`lang-${l}`}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${lang === l ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        <button onClick={() => setLeave("break")} data-testid="break-button" title={t("break_hint")}
          className="flex items-center gap-1.5 rounded-full border border-amber-200 dark:border-amber-900 bg-amber-50/70 dark:bg-amber-950/40 px-2 py-1.5 sm:px-3 text-xs font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
          <Coffee className="h-4 w-4" /><span className="hidden md:inline">{t("break")}</span>
        </button>

        <button onClick={() => setLeave("logout")} data-testid="logout-button" title={t("logout")}
          className="flex items-center gap-1.5 rounded-full border border-rose-200 dark:border-rose-900 bg-rose-50/70 dark:bg-rose-950/40 px-2 py-1.5 sm:px-3 text-xs font-semibold text-rose-600 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors">
          <LogOut className="h-4 w-4" /><span className="hidden md:inline">{t("logout")}</span>
        </button>

        <div className="flex items-center gap-2.5 sm:pl-1">
          <div className="hidden lg:block text-right">
            <p className="text-sm font-semibold text-foreground leading-none">{user?.nickname || user?.name || "Beheerder"}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{user?.email}</p>
          </div>
          <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold">{initials}</div>
        </div>
      </div>
      <Dialog open={!!leave} onOpenChange={(o) => !o && setLeave(null)}>
        <DialogContent className="sm:max-w-md" data-testid="shift-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t(leave === "break" ? "break" : "logout")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t("shift_note_hint")}</p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder={t("shift_note_ph")} data-testid="shift-note-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeave(null)} data-testid="shift-note-cancel">{t("cancel")}</Button>
            <Button onClick={() => logout(leave, note)} data-testid="shift-note-confirm">
              {t(leave === "break" ? "break" : "logout")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
